import { useEffect, useRef, useState } from 'react';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Icon } from '../../../components/icons/Icon';
import { useTranslation } from '../../../providers/UIProvider';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { SEVERITY_CONFIG, cn } from '../../../utils';
import { SeverityIcon } from './SeverityIcon';
import { IssueRow } from './IssueRow';
import { GroupedRowItem } from './GroupedRowItem';
import type { SeverityGroup } from './types';

export interface SeverityBannerProps {
  group: SeverityGroup;
  onScrollTo?: (target: string) => void;
}

/**
 * One full-width section of the banner: header (severity title + count badge +
 * chevron) plus an animated, expandable body listing the section's rows.
 *
 * Defaults to expanded on desktop, collapsed on mobile.
 */
export function SeverityBanner({ group, onScrollTo }: SeverityBannerProps) {
  const t = useTranslation('components.validationBanner');
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(!isMobile);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const cfg = SEVERITY_CONFIG[group.severity];

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const update = () => setContentHeight(el.scrollHeight);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [group]);

  return (
    <div
      className={`rounded-xl ${cfg.bgColor} overflow-hidden`}
      role='region'
      aria-label={t(`severity.${group.severity}`)}
    >
      <Button
        variant='ghost'
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        className={cn(
          'w-full h-auto min-h-0 px-md py-sm rounded-none justify-start',
          'hover:bg-transparent active:bg-transparent',
          'hover:brightness-[0.96] active:brightness-[0.93]',
          'transition-[filter] duration-150',
          '[&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:gap-sm [&>span]:text-left [&>span]:whitespace-normal'
        )}
      >
        <SeverityIcon severity={group.severity} className='h-3.5 w-3.5 shrink-0' />
        <span className={`text-sm font-semibold ${cfg.iconColor} flex-1 min-w-0`}>
          {t(`severity.${group.severity}`)}
        </span>
        <Badge size='sm' color={cfg.badgeColor} className='flex items-center gap-xs shrink-0'>
          <Icon name={cfg.icon} className='h-3 w-3' />
          <span>{group.count}</span>
        </Badge>
        <Icon
          name='chevron-down'
          className={`h-3.5 w-3.5 ${cfg.iconColor} opacity-60 transition-transform duration-300 shrink-0 ${expanded ? 'rotate-180' : ''}`}
          aria-hidden='true'
        />
      </Button>

      <div
        style={{ maxHeight: expanded ? `${contentHeight}px` : '0px' }}
        className='transition-[max-height] duration-300 ease-in-out overflow-hidden'
      >
        <div ref={contentRef} className='px-md pb-sm pt-2xs space-y-2xs'>
          {group.flatIssues.map((issue, idx) => (
            <IssueRow
              key={`${issue.code ?? 'noCode'}-${issue.path ?? ''}-${idx}`}
              issue={issue}
              onScrollTo={onScrollTo}
            />
          ))}
          {group.groups.map(g => (
            <GroupedRowItem key={g.groupId} group={g} onScrollTo={onScrollTo} />
          ))}
        </div>
      </div>
    </div>
  );
}
